import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { UserMinus, AlertCircle } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { selfAPI } from '../services/api';
import { formatCurrency } from '../utils/formatters';

export default function EmployeeFamilyRemove() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [member, setMember] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [declaration, setDeclaration] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    selfAPI.policy()
      .then(({ data }) => {
        setPolicy(data);
        const found = data?.removableDependents?.find((d) => String(d.id) === String(memberId));
        setMember(found || null);
      })
      .finally(() => setLoading(false));
  }, [memberId]);

  useEffect(() => {
    if (!member || !policy) return undefined;
    const timer = setTimeout(async () => {
      try {
        const { data } = await selfAPI.previewFamilyPremium({
          changes: [{ action: 'remove', family_member_id: member.id }],
        });
        if (data.error) {
          setPreview(null);
          setPreviewError(data.error);
        } else {
          setPreview(data);
          setPreviewError(null);
        }
      } catch (err) {
        setPreview(null);
        setPreviewError(err.response?.data?.error || 'Unable to calculate premium');
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [member, policy]);

  const handleSubmit = async () => {
    if (!declaration || !member) return;
    setSubmitting(true);
    setSubmitError(null);
    const idempotencyKey = `rem-${Date.now()}-${member.id}`;
    try {
      const { data } = await selfAPI.submitFamilyRequest({
        declarationAccepted: true,
        changes: [{ action: 'remove', family_member_id: member.id }],
      }, idempotencyKey);
      navigate('/', { state: { message: data.message } });
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!policy) return <p className="text-slate-500">Policy not found.</p>;
  if (!member) {
    return (
      <div className="space-y-4">
        <Link to="/" className="text-primary-600 text-sm">← Back to My Coverage</Link>
        <p className="text-slate-500">Dependent not found or cannot be removed.</p>
      </div>
    );
  }

  const hasPending = policy.pendingRequest;

  return (
    <div className="space-y-6 max-w-3xl">
      <Link to="/" className="text-primary-600 text-sm">← Back to My Coverage</Link>
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <UserMinus className="w-7 h-7 text-primary-600" />
          Remove Family Member
        </h1>
        <p className="text-slate-500 mt-1">
          Request removal of a dependent — premium refund applies after Super Admin approval
        </p>
      </div>

      {hasPending && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          Pending request <strong>{policy.pendingRequest.request_number}</strong> — cancel or wait for approval before submitting another.
          <Link to={`/my-family/requests/${policy.pendingRequest.id}`} className="block text-primary-700 font-medium mt-1 underline">
            View pending request
          </Link>
        </div>
      )}

      {!hasPending && (
        <>
          <div className="card text-sm space-y-2">
            <p><strong>Member:</strong> {member.member_name}</p>
            <p><strong>Relation:</strong> {member.relation?.replace(/_/g, ' ')}</p>
            <p><strong>Current contribution:</strong> {formatCurrency(member.total)}</p>
          </div>

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3 text-sm text-amber-900">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <p>Removal requests can be submitted at any time. Refund amount is calculated pro-rata based on policy terms.</p>
          </div>

          {(preview || previewError) && (
            <div className={`card text-sm ${previewError ? 'border-red-200 bg-red-50' : 'border-primary-100 bg-primary-50'}`}>
              <h3 className="font-semibold mb-3">Premium Impact Preview</h3>
              {previewError && <p className="text-red-700">{previewError}</p>}
              {preview && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <span className="text-slate-500 block">Current Premium</span>
                    <strong>{formatCurrency(preview.currentPremium)}</strong>
                  </div>
                  <div>
                    <span className="text-slate-500 block">New Premium</span>
                    <strong>{formatCurrency(preview.proposedPremium)}</strong>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Refund</span>
                    <strong className="text-green-700">{formatCurrency(Math.abs(preview.difference))}</strong>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            className="btn-primary"
            disabled={!preview || previewError}
            onClick={() => setShowConfirm(true)}
          >
            Review & Submit Removal Request
          </button>
        </>
      )}

      {showConfirm && preview && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4">
            <h3 className="text-lg font-bold">Confirm Removal Request</h3>
            <div className="text-sm space-y-2">
              <p><strong>Member:</strong> {member.member_name} ({member.relation?.replace(/_/g, ' ')})</p>
              <p><strong>Current Premium:</strong> {formatCurrency(preview.currentPremium)}</p>
              <p><strong>New Premium:</strong> {formatCurrency(preview.proposedPremium)}</p>
              <p><strong>Estimated Refund:</strong> {formatCurrency(Math.abs(preview.difference))}</p>
            </div>
            <label className="flex gap-2 text-sm items-start">
              <input type="checkbox" checked={declaration} onChange={(e) => setDeclaration(e.target.checked)} className="mt-1" />
              <span>I confirm that I want to remove this dependent from my policy coverage.</span>
            </label>
            {submitError && <p className="text-red-600 text-sm">{submitError}</p>}
            <div className="flex gap-3">
              <button type="button" className="btn-primary flex-1" disabled={!declaration || submitting} onClick={handleSubmit}>
                {submitting ? 'Submitting…' : 'Confirm & Submit'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setShowConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
